import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Clock, CheckCircle, AlertCircle, Calendar, Instagram } from "lucide-react";
import SEO from "@/components/SEO";

const StrassDentaire = () => {
  const galleryImages = [
    "https://i.ibb.co/wZjg8th4/F1-DC93-B3-B119-47-B7-9481-92-CDEB587-D76.avif",
    "https://i.ibb.co/XZ57TbKC/E99-B311-D-BD67-44-C3-81-A5-FA5-C59900805.avif",
    "https://i.ibb.co/WNhZCxWx/7-E891392-2154-45-BC-A5-FF-038-CA1586536.avif",
    "https://i.ibb.co/23CTjvTX/8-DEA5-ED6-4544-4-F7-F-B180-C1-F54-E757544.avif",
    "https://i.ibb.co/dJ0GdQFV/5072-A21-F-5885-4900-97-AD-5-C81-BAF7-FFDB.avif",
    "https://i.ibb.co/MkRM5KC6/A9-D40-A62-9-DA0-45-FE-BC23-EB77-CB8-D7466.avif",
    "https://i.ibb.co/DPB6ZGX6/A7917-EC7-342-E-4-B80-8923-02-CF005-EF1-B3.avif",
    "https://i.ibb.co/G3GKDmbH/AB8-CBBCF-1-F59-40-C7-87-D6-020-B94-A64154.png",
    "https://i.ibb.co/yc9DRchj/BE98-A590-6-A6-F-4-F91-A43-B-7-C5320-E385-A6.jpg",
    "https://i.ibb.co/JRY0h2Jq/F62-ED61-E-17-DA-40-B3-93-CE-FD3-B5-ACE8-F66.avif",
  ];

  const steps = [
    { number: "01", title: "Choix du/des strass", description: "Sélection du modèle, de la forme et de l'emplacement souhaité" },
    { number: "02", title: "Nettoyage et préparation", description: "Préparation minutieuse de la surface dentaire" },
    { number: "03", title: "Pose précise", description: "Application délicate du strass sur la dent" },
    { number: "04", title: "Fixation et vérification", description: "Sécurisation et contrôle final du résultat" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Strass Dentaire" 
        description="Pose de strass dentaire esthétique aux Sables-d'Olonne. Indolore, sans perçage, réversible. Sublimez votre sourire avec une finition délicate."
        keywords="strass dentaire Les Sables-d'Olonne, bijou dentaire Vendée, pose strass dent, tooth gem, strass dentaire esthétique"
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://i.ibb.co/XZ57TbKC/E99-B311-D-BD67-44-C3-81-A5-FA5-C59900805.avif"
            alt="Strass dentaire"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading mb-6 leading-tight">
            Strass dentaire
          </h1>
          <p className="text-xl sm:text-2xl text-muted-foreground font-light mb-4">
            Une finition délicate pour sublimer votre sourire.
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Pose de strass dentaire esthétique, éphémère, sans perçage et sans douleur.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/rendez-vous">
              <Button size="lg" className="px-8">
                <Calendar className="w-4 h-4 mr-2" />
                Prendre rendez-vous
              </Button>
            </Link>
            <a
              href="https://www.instagram.com/twinkietooth/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border border-border/40 hover:border-foreground/40 transition-colors"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm tracking-wider">@twinkietooth</span>
            </a>
          </div>
        </div>
      </section>

      {/* Qu'est-ce que le strass dentaire ? */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading mb-6">
                Qu'est-ce que le strass dentaire ?
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Le strass dentaire est une pose esthétique qui permet d'ajouter une touche de brillance à votre sourire. 
                Cette prestation est totalement réversible et respectueuse de votre émail lorsqu'elle est correctement réalisée.
              </p>
              
              <div className="space-y-4">
                {[
                  "Pose esthétique et délicate",
                  "Totalement indolore",
                  "Sans perçage de la dent",
                  "Réversible à tout moment",
                  "Respectueuse de l'émail dentaire",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://i.ibb.co/wZjg8th4/F1-DC93-B3-B119-47-B7-9481-92-CDEB587-D76.avif"
                alt="Strass dentaire exemple"
                className="w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Déroulement de la pose */}
      <section className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading mb-4">
              Déroulement de la pose
            </h2>
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="w-5 h-5" />
              <span>Durée : 10 à 30 minutes</span>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-heading text-muted-foreground/30 mb-4">
                  {step.number}
                </div>
                <h3 className="font-medium mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prendre rendez-vous */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading mb-6">
            Prendre rendez-vous
          </h2>
          <p className="text-muted-foreground mb-4">
            Cette prestation est réalisée sur rendez-vous ou au passage selon les disponibilités.
          </p>
          <p className="text-muted-foreground mb-8">
            N'hésitez pas à me contacter pour toute question ou pour réserver votre créneau.
          </p>
          
          <Link to="/rendez-vous">
            <Button size="lg" className="px-10">
              <Calendar className="w-4 h-4 mr-2" />
              Prendre rendez-vous
            </Button>
          </Link>
        </div>
      </section>

      {/* Contre-indications */}
      <section className="py-20 px-4 bg-card/30">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start gap-4 mb-8">
            <AlertCircle className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading mb-4">
                Contre-indications & informations importantes
              </h2>
              <p className="text-muted-foreground mb-6">
                Pour votre sécurité et le bon déroulement de la prestation, certaines conditions doivent être respectées.
              </p>
            </div>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              "Caries non soignées",
              "Problèmes dentaires en cours de traitement",
              "En cas de doute → avis d'un professionnel de santé recommandé",
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-muted-foreground">
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          
          <div className="p-4 border border-border/40 bg-background/50">
            <p className="text-sm text-muted-foreground italic">
              Cette prestation est esthétique et ne constitue pas un acte médical.
            </p>
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-heading mb-12 text-center">
            Tarifs
          </h2>
          
          <div className="space-y-12">
            {/* Strass ronds */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Strass ronds
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">1 strass rond</span>
                  <span className="font-medium">20 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">2 strass ronds</span>
                  <span className="font-medium">30 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">Strass rond supplémentaire</span>
                  <span className="font-medium">+5 €</span>
                </div>
              </div>
            </div>

            {/* Strass formes */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Strass formes
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">1 strass forme</span>
                  <span className="font-medium">30 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">2 strass formes</span>
                  <span className="font-medium">45 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">Strass forme supplémentaire</span>
                  <span className="font-medium">+10 €</span>
                </div>
              </div>
            </div>

            {/* Mix */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Mix
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">1 strass rond + 1 strass forme</span>
                  <span className="font-medium">40 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">2 strass ronds + 2 strass formes</span>
                  <span className="font-medium">55 €</span>
                </div>
              </div>
            </div>

            {/* Mini compositions */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Minis compositions
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">Papillon</span>
                  <span className="font-medium">55 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <span className="text-muted-foreground">Ligne de strass</span>
                  <span className="font-medium">40 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <div>
                    <span className="text-muted-foreground">Contour de dent</span>
                    <p className="text-xs text-muted-foreground/70 mt-1">(9/15 ronds selon la dent et taille de strass)</p>
                  </div>
                  <span className="font-medium">60 à 75 €</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/30">
                  <div>
                    <span className="text-muted-foreground">Dent entière (effet full strass)</span>
                    <p className="text-xs text-muted-foreground/70 mt-1">(11 à 20 strass ronds selon la dent et taille de strass)</p>
                  </div>
                  <span className="font-medium">70 à 90 €</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4 italic">
                → Prix ajusté selon la taille et la complexité
              </p>
            </div>

            {/* Compositions personnalisées */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Compositions personnalisées
              </h3>
              <div className="p-4 border border-border/40 bg-card/30">
                <p className="text-muted-foreground">
                  Compositions avec plusieurs strass — Prix dégressif, sur devis
                </p>
              </div>
            </div>

            {/* Bijoux dentaires */}
            <div>
              <h3 className="text-xl font-heading mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Bijoux dentaires
              </h3>
              <div className="flex justify-between items-center py-3 border-b border-border/30">
                <div>
                  <span className="text-muted-foreground">Bijou or 18 carats blanc ou jaune</span>
                  <p className="text-xs text-muted-foreground/70 mt-1">(sur commande)</p>
                </div>
                <span className="font-medium">À partir de 75 €</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galerie */}
      <section className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-heading mb-12 text-center">
            Galerie
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryImages.map((src, index) => (
              <div key={index} className="aspect-square overflow-hidden group">
                <img
                  src={src}
                  alt={`Strass dentaire exemple ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading mb-6">
            Envie de sublimer votre sourire ?
          </h2>
          <p className="text-muted-foreground mb-8">
            Réservez dès maintenant votre rendez-vous pour une pose de strass dentaire.
          </p>
          <Link to="/rendez-vous">
            <Button size="lg" className="px-10">
              Prendre rendez-vous
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StrassDentaire;
